import { BaseApiService } from '@/services/base-service';
import {
  ApiPagination, BaseResponse, Filters, FiltersPagination, Response,
} from '@/types';
import { MarketingFund } from '../types';

interface MarketingFundList extends BaseResponse, ApiPagination {
  data: {
    items: MarketingFund.Aplication[],
    marketing_fund_limit: number
  }
}

interface MarketingFundApplication extends BaseResponse {
  data: MarketingFund.AplicationDetail,
}

interface MarketingFundApplicationHistoryList extends BaseResponse, ApiPagination {
  data: MarketingFund.History[],
}

interface MarketingFundEvent {
    status: any;
    data: MarketingFund.Event,
}

interface MarketingFundEvents extends BaseResponse, ApiPagination {
  data: {
    items: MarketingFund.Event[],
  }
}

interface MarketingFundTypes extends BaseResponse {
    items: MarketingFund.Type[],
}

interface MarketingFundReport extends BaseResponse {
  data: MarketingFund.ReportForm,
}
interface MarketingFundEventReport extends BaseResponse {
  data: MarketingFund.EventReport,
}

interface MarketingFundSizeAudienceReached extends BaseResponse {
    items: MarketingFund.SizeAudience[],
}

export class MarketingFundService extends BaseApiService {
  private readonly base_path = 'partner/marketing-fund'

  async get_aplications(filters:FiltersPagination<Filters>): Promise<MarketingFundList> {
    const payload = filters;

    const { data } = await this.http_client.get<MarketingFundList>(this.base_path, {
      params: {
        filters: payload.filters,
        limit: payload.pagination.limit,
        page: payload.pagination.page,
        sort: payload.pagination.sort,
        sort_type: payload.pagination.sort_type,
      },
    });

    return data;
  }

  async get_events(filters:FiltersPagination<Filters>): Promise<MarketingFundEvents> {
    const url = `${this.base_path}/events`;
    const payload = filters;

    const { data } = await this.http_client.get<MarketingFundEvents>(url, {
      params: {
        filters: payload.filters,
        limit: payload.pagination.limit,
        page: payload.pagination.page,
        sort: payload.pagination.sort,
        sort_type: payload.pagination.sort_type,
      },
    });

    return data;
  }

  async get_events_of_application(filters:FiltersPagination<Filters>): Promise<MarketingFundEvents> {
    const url = `${this.base_path}/events`;
    const payload = filters;

    const { data } = await this.http_client.get<MarketingFundEvents>(url, {
      params: {
        filters: payload.filters,
        limit: payload.pagination.limit,
        page: payload.pagination.page,
        sort: payload.pagination.sort,
        sort_type: payload.pagination.sort_type,
      },
    });

    return data;
  }

  async get_aplication_by_id(id: number): Promise<Response<MarketingFund.AplicationDetail>> {
    const url = `${this.base_path}/${id}`;
    const { data } = await this.http_client.get<Response<MarketingFund.AplicationDetail>>(url);

    return data;
  }

  async get_event_by_id(id: string | number): Promise<Response<MarketingFund.Event>> {
    const url = `${this.base_path}/events/${id}`;
    const { data } = await this.http_client.get<Response<MarketingFund.Event>>(url);

    return data;
  }

  async get_history_of_application(filters:FiltersPagination<Filters>, id: string | number): Promise<MarketingFundApplicationHistoryList> {
    const url = `${this.base_path}/${id}/history`;
    const payload = filters;

    const { data } = await this.http_client.get<MarketingFundApplicationHistoryList>(url, {
      params: {
        filters: payload.filters,
        limit: payload.pagination.limit,
        page: payload.pagination.page,
      },
    });

    return data;
  }

  async add_aplication() {
    const url = `${this.base_path}`;

    const { data } = await this.http_client.post<Response<MarketingFund.AplicationDetail>>(url);

    return data;
  }

  async add_event(this_payload: FormData, id: number | string):
  Promise<Response<MarketingFundEvent>> {
    const url = `${this.base_path}/${id}/events`;
    const payload = this_payload;

    const { data } = await this.http_client.post<Response<MarketingFundEvent>>(url, payload);

    return data;
  }

  async add_files(id: number | string, payload_form_data: FormData) {
    const url = `${this.base_path}/events/${id}/file`;

    const { data } = await this.http_client.post<Response<any>>(url, payload_form_data);

    return data;
  }

  async edit_event(payload_formdata: FormData, event_id: number | string, id: number | string):
  Promise<Response<MarketingFund.Event>> {
    const url = `${this.base_path}/events/${event_id}`;
    const payload = payload_formdata;

    const { data } = await this.http_client.patch<Response<MarketingFund.Event>>(url, payload);

    return data;
  }

  async delete_event(event_id: string | number) {
    const url = `${this.base_path}/events/${event_id}`;

    const data = await this.http_client.delete<Response<MarketingFund.Event>>(url);

    return data;
  }

  async delete_file(file_id: number, event_id: number) {
    const url = `${this.base_path}/events/${event_id}/file/${file_id}`;

    const data = await this.http_client.delete<Response<MarketingFund.Event>>(url);

    return data;
  }

  async get_event_types_list(): Promise<Response<MarketingFundTypes>> {
    const url = `${this.base_path}/events/types`;
    const { data } = await this.http_client.get<Response<MarketingFundTypes>>(url);

    return data;
  }

  /**
   * Отправка отчета по меропиятию
   */
  async add_event_report(event_id: string | number, payload: MarketingFund.ReportForm):
    Promise<Response<MarketingFundReport>> {
    const url = `${this.base_path}/events/${event_id}/report`;

    const { data } = await this.http_client.post<Response<MarketingFundReport>>(url, payload);

    return data;
  }

  /**
   * Получение отчетов по мероприятию
   */
  async get_event_reports(event_id: number): Promise<MarketingFundEventReport> {
    const url = `${this.base_path}/events/${event_id}/report`;

    const { data } = await this.http_client.get<Response<MarketingFund.EventReport>>(url);

    return data;
  }

  async send_to_atol_for_approval(id: number) {
    const url = `${this.base_path}/${id}`;

    const { data } = await this.http_client.patch<Response<MarketingFund.AplicationDetail>>(url);

    return data;
  }

  async get_size_audience_reached(): Promise<Response<MarketingFundSizeAudienceReached>> {
    const url = `${this.base_path}/events/size-audience-reached`;

    const { data } = await this.http_client.get<Response<MarketingFundSizeAudienceReached>>(url);

    return data;
  }
}
